// src/components/plan/PlanChoosePage.jsx
// Pooled inbox SP5 — /plan, Choose your plan (proto plan/package.html; spec
// § Screens per app → Web). Mounted inside <OnboardingGate stageKey="intake">
// (PlanRoutes), so a client still before the questions is sent to their own
// page; any client at or past them may choose (the server's pastDocuments).
// Every active plan from GET /billing/plans, in the server's order, four
// columns → two → one; Essentials carries the primary button and no tag. A
// client with chat access is changing plan (reached from Profile): their own
// plan shows "Your plan" and a disabled "Current Plan", another plan is
// PATCH /billing/plan and back to Profile. Otherwise Get Started records the
// choice (POST /billing/plan) and opens /plan/payment.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { Button, Pass } from "../ui";
import Loader from "../Loader";
import {
  GENERIC_ERROR,
  LOAD_FAILED,
  TRY_AGAIN,
} from "../onboarding/onboardingCopy";
import { isWrongStage, useStageNavigation } from "../onboarding/onboardingNav";
import { QUESTIONS_PATH, intakePath } from "../intake/intakeNav";
import { hasChatAccess } from "../../shared/_helpers/chatGate";
import {
  changePlan,
  choosePlan,
  getPlans,
} from "../../shared/_services/billing.services";
import PlanLayout from "./PlanLayout";
import { PLAN_PAYMENT_PATH, useBillingResult } from "./planNav";
import {
  APPLE_BILLING_NOTE,
  APPLE_MANAGE_URL,
  CANCELLED_NO_CHANGE,
  CARD_DECLINED_WITH,
  COMPED_NOTE,
  CONFLICT_RETRY,
  CURRENT_PLAN,
  GET_STARTED,
  MANAGE_IN_APP_STORE,
  NO_PACKAGES,
  PER_MONTH,
  PLAN_BACK,
  PLAN_FOOTNOTE,
  PLAN_FREE_BODY,
  PLAN_FREE_TITLE,
  PLAN_RETURNING_BODY,
  PLAN_RETURNING_TITLE,
  PLAN_SUB,
  PLAN_TITLE,
  WHATS_INCLUDED,
  YOUR_PLAN_PILL,
  money,
} from "./planCopy";

const PROFILE_PATH = "/profile";
const LAST_QUESTION = "q7_about_you";

// The entitled client's own plan: by tier when the plan has one, else by name.
const isCurrentPlan = (plan, entitlement) =>
  entitlement?.tier
    ? plan.tier === entitlement.tier
    : plan.name === entitlement?.planName;

export default function PlanChoosePage({ stage }) {
  const navigate = useNavigate();
  const go = useStageNavigation();
  const { user } = useAuth();
  const settle = useBillingResult();
  const changing = hasChatAccess(user);
  const h1 = useRef(null);
  const [load, setLoad] = useState({ status: "loading", data: null });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [manageUrl, setManageUrl] = useState(null);
  const isApple = changing && user.entitlement?.source === "apple";
  const isCanceling = changing && user.entitlement?.state === "canceling";
  // A comped client's plan is the server's to change (it refuses the change).
  const isComped = changing && user.entitlement?.source === "admin";
  const locked = isApple || isCanceling || isComped;

  const read = useCallback(async () => {
    setLoad({ status: "loading", data: null });
    try {
      const res = await getPlans();
      setLoad({ status: "ready", data: res.data });
    } catch (_) {
      setLoad({ status: "error", data: null });
    }
  }, []);
  useEffect(() => {
    read();
  }, [read]);
  useEffect(() => {
    if (load.status === "ready") h1.current?.focus();
  }, [load.status]);

  // Back: Profile when changing plan; Q7 while the questions are still
  // running; the Questions page once they are behind the client.
  const back = changing
    ? PROFILE_PATH
    : stage?.stage === "intake"
      ? intakePath(LAST_QUESTION)
      : QUESTIONS_PATH;

  const choose = async (plan) => {
    setBusy(plan.id);
    setError(null);
    setManageUrl(null);
    try {
      if (changing) {
        const res = await changePlan(plan.id);
        await settle(res.data.entitlement);
        navigate(PROFILE_PATH);
      } else {
        await choosePlan(plan.id);
        navigate(PLAN_PAYMENT_PATH);
      }
    } catch (err) {
      if (isWrongStage(err)) {
        go(err.response.data);
        return;
      }
      if (err?.response?.data?.code === "unknown_plan") read();
      const data = err?.response?.data;
      if (data?.code === "manage_in_app_store") {
        setManageUrl(data.manageUrl || APPLE_MANAGE_URL);
        setError(APPLE_BILLING_NOTE);
      } else if (data?.code === "card_declined") {
        setError(CARD_DECLINED_WITH(data.declineCode));
      } else if (data?.code === "conflict") {
        setError(CONFLICT_RETRY);
      } else {
        setError(data?.message || GENERIC_ERROR);
      }
      setBusy(null);
    }
  };

  if (load.status === "loading")
    return (
      <PlanLayout width="wide">
        <Loader />
      </PlanLayout>
    );
  if (load.status === "error")
    return (
      <PlanLayout width="wide">
        <div role="alert" className="tw-grid tw-justify-items-center tw-gap-4">
          <p className="tw-m-0">{LOAD_FAILED}</p>
          <Button variant="quiet" onClick={read}>
            {TRY_AGAIN}
          </Button>
        </div>
      </PlanLayout>
    );

  const { plans, freeMonth, setupFee, addOnMeetingPriceCents } = load.data;
  return (
    <PlanLayout width="wide">
      <header className="tw-mb-8 tw-grid tw-justify-items-center tw-gap-3 tw-text-center">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            as={Link}
            to={back}
            variant="icon"
            iconOnly
            aria-label={PLAN_BACK}
            className="tw-border-transparent"
          >
            <span aria-hidden="true">←</span>
          </Button>
          <h1
            ref={h1}
            tabIndex={-1}
            className="tw-m-0 tw-text-2xl tw-font-bold focus:tw-outline-none"
          >
            {PLAN_TITLE}
          </h1>
        </div>
        <p className="tw-m-0 tw-max-w-xl tw-text-md tw-text-text-muted">
          {PLAN_SUB}
        </p>
      </header>

      {!changing && (
        <div className="tw-mx-auto tw-mb-8 tw-flex tw-max-w-2xl tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-4">
          <span aria-hidden="true" className="tw-text-text-accent">
            ✦
          </span>
          <p className="tw-m-0 tw-text-sm tw-text-text-muted">
            <b className="tw-block tw-text-text">
              {freeMonth ? PLAN_FREE_TITLE : PLAN_RETURNING_TITLE}
            </b>
            {freeMonth ? PLAN_FREE_BODY(setupFee?.cents) : PLAN_RETURNING_BODY}
          </p>
        </div>
      )}

      {locked && (
        <p
          role="status"
          className="tw-mx-auto tw-mb-8 tw-mt-0 tw-max-w-2xl tw-text-center tw-text-sm tw-text-text-muted"
        >
          {isApple
            ? APPLE_BILLING_NOTE
            : isComped
              ? COMPED_NOTE
              : CANCELLED_NO_CHANGE}
          {isApple && (
            <>
              {" "}
              <a
                href={APPLE_MANAGE_URL}
                className="tw-text-text-accent tw-underline"
              >
                {MANAGE_IN_APP_STORE}
              </a>
            </>
          )}
        </p>
      )}

      {plans.length === 0 ? (
        <div className="tw-mx-auto tw-max-w-md tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-8 tw-text-center">
          <p className="tw-m-0">{NO_PACKAGES}</p>
        </div>
      ) : (
        <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-6 tw-p-0 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {plans.map((plan) => {
            const current = changing && isCurrentPlan(plan, user.entitlement);
            return (
              <li key={plan.id} className="tw-min-w-0">
                <Pass
                  kind={plan.name}
                  className="tw-h-full"
                  stub={
                    <div className="tw-grid tw-w-full tw-gap-3">
                      <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-text-muted">
                        {WHATS_INCLUDED}
                      </p>
                      <ul className="tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0 tw-text-sm">
                        {plan.features.map((feature) => (
                          <li key={feature} className="tw-flex tw-gap-2">
                            <span
                              aria-hidden="true"
                              className="tw-text-text-accent"
                            >
                              ✓
                            </span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  {current && (
                    <span className="tw-self-start tw-rounded-pill tw-border tw-border-solid tw-border-divider tw-px-2 tw-py-px tw-text-xs tw-font-semibold">
                      {YOUR_PLAN_PILL}
                    </span>
                  )}
                  <div>
                    <p
                      id={`plan-price-${plan.id}`}
                      className="tw-m-0 tw-flex tw-items-baseline tw-gap-2"
                    >
                      <b className="tw-text-3xl tw-font-bold tw-tabular-nums">
                        {money(plan.priceCents)}
                      </b>
                      <span className="tw-text-sm tw-text-text-muted">
                        {PER_MONTH}
                      </span>
                    </p>
                    {plan.description && (
                      <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-text-muted">
                        {plan.description}
                      </p>
                    )}
                  </div>
                  {current ? (
                    <Button disabled className="tw-w-full">
                      {CURRENT_PLAN}
                    </Button>
                  ) : locked ? null : (
                    <Button
                      aria-describedby={`plan-price-${plan.id}`}
                      variant={plan.tier === "essential" ? "primary" : "ghost"}
                      className="tw-w-full"
                      loading={busy === plan.id}
                      disabled={Boolean(busy) && busy !== plan.id}
                      onClick={() => choose(plan)}
                    >
                      {GET_STARTED} <span aria-hidden="true">→</span>
                    </Button>
                  )}
                </Pass>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          role="alert"
          className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-danger"
        >
          {error}
          {manageUrl && (
            <>
              {" "}
              <a href={manageUrl} className="tw-text-text-accent tw-underline">
                {MANAGE_IN_APP_STORE}
              </a>
            </>
          )}
        </p>
      )}
      <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-text-muted">
        {PLAN_FOOTNOTE(addOnMeetingPriceCents)}
      </p>
    </PlanLayout>
  );
}
